import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { useServerFn } from "@tanstack/react-start";

import appCss from "../styles.css?url";
import antonLatin400 from "@fontsource/anton/files/anton-latin-400-normal.woff2?url";
import poppinsLatin500 from "@fontsource/poppins/files/poppins-latin-500-normal.woff2?url";
import poppinsLatin700 from "@fontsource/poppins/files/poppins-latin-700-normal.woff2?url";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { MobileTabBar } from "@/components/MobileTabBar";
import { LanguageProvider } from "@/lib/i18n";
import { ContentProvider } from "@/lib/content";

import { trackPageView } from "@/lib/analytics.functions";


function NotFoundComponent() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-charcoal px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-7xl sm:text-8xl md:text-[10rem] leading-none text-yellow">404</h1>
        <h2 className="mt-2 text-xl uppercase tracking-widest text-cream">Lost in the sauce</h2>
        <p className="mt-2 text-sm text-cream/60">This page doesn't exist — but the tacos do.</p>
        <div className="mt-6">
          <Link to="/" className="inline-flex bg-chili text-cream px-6 py-3 text-xs uppercase tracking-widest font-bold hover:bg-yellow hover:text-charcoal transition-colors">
            Back home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  return (
    <div className="flex min-h-dvh items-center justify-center bg-charcoal px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl uppercase tracking-widest text-cream">Something burned</h1>
        <p className="mt-2 text-sm text-cream/60">Try refreshing or head back home.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="bg-yellow text-charcoal px-5 py-2.5 text-xs uppercase tracking-widest font-bold"
          >
            Try again
          </button>
          <a href="/" className="border border-border px-5 py-2.5 text-xs uppercase tracking-widest text-cream">Home</a>
        </div>
      </div>
    </div>
  );
}

const SITE_URL = "https://tacoya.snapinsolutions.com";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Tacoya — The New Generation of Nordic Street Food" },
      { name: "description", content: "Helsinki-born birrieria & street food brand. Authentic Mexican soul, viral momentum, scaling across the Nordics." },
      { name: "author", content: "Tacoya" },
      { property: "og:title", content: "Tacoya — The New Generation of Nordic Street Food" },
      { property: "og:description", content: "Helsinki-born birrieria & street food brand. Authentic Mexican soul, viral momentum, scaling across the Nordics." },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Tacoya" },
      { property: "og:locale", content: "en_US" },
      { property: "og:locale:alternate", content: "fi_FI" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@tacoya" },
      { name: "theme-color", content: "#1a1410" },
      { name: "twitter:title", content: "Tacoya — The New Generation of Nordic Street Food" },
      { name: "twitter:description", content: "Helsinki-born birrieria & street food brand. Authentic Mexican soul, viral momentum, scaling across the Nordics." },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", href: "/tacoya-logo.png" },
      { rel: "apple-touch-icon", href: "/tacoya-logo.png" },
      // Fonts self-hosted via @fontsource (see src/styles.css) — no render-blocking Google Fonts request.
      { rel: "preload", as: "font", type: "font/woff2", href: antonLatin400, crossOrigin: "anonymous" },
      { rel: "preload", as: "font", type: "font/woff2", href: poppinsLatin500, crossOrigin: "anonymous" },
      { rel: "preload", as: "font", type: "font/woff2", href: poppinsLatin700, crossOrigin: "anonymous" },
      {
        rel: "preload",
        as: "image",
        href: "/__l5e/assets-v1/baf57ef9-b687-4700-8970-d6c01b57a7a8/hero-birria.webp",
        imageSrcSet:
          "/__l5e/assets-v1/35fc884b-9f8c-420e-8c37-5d67765f8d53/hero-birria-mobile.webp 900w, /__l5e/assets-v1/baf57ef9-b687-4700-8970-d6c01b57a7a8/hero-birria.webp 1600w",
        imageSizes: "100vw",
        fetchpriority: "high",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": `${SITE_URL}/#organization`,
              name: "Tacoya",
              url: SITE_URL,
              logo: `${SITE_URL}/tacoya-logo.png`,
              description: "Helsinki-born birrieria and street food brand serving slow-cooked birria, quesabirria and Nordic-Mexican fusion.",
              sameAs: [
                "https://www.instagram.com/tacoya",
                "https://www.tiktok.com/@tacoya",
              ],
              areaServed: { "@type": "Country", name: "Finland" },
            },
            {
              "@type": "WebSite",
              "@id": `${SITE_URL}/#website`,
              url: SITE_URL,
              name: "Tacoya",
              description: "The new generation of Nordic street food.",
              publisher: { "@id": `${SITE_URL}/#organization` },
              inLanguage: ["en", "fi"],
              potentialAction: {
                "@type": "SearchAction",
                target: `${SITE_URL}/menu?q={search_term_string}`,
                "query-input": "required name=search_term_string",
              },
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head><HeadContent /></head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isChrome = !pathname.startsWith("/admin") && !pathname.startsWith("/auth");
  const track = useServerFn(trackPageView);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;
    void import("@/integrations/supabase/client").then(({ supabase }) => {
      if (cancelled) return;
      const { data: sub } = supabase.auth.onAuthStateChange((event) => {
        if (event !== "SIGNED_IN" && event !== "SIGNED_OUT" && event !== "USER_UPDATED") return;
        router.invalidate();
        if (event !== "SIGNED_OUT") queryClient.invalidateQueries();
      });
      unsubscribe = () => sub.subscription.unsubscribe();
    });
    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [router, queryClient]);

  useEffect(() => {
    if (pathname.startsWith("/admin") || pathname.startsWith("/auth")) return;
    track({ data: { path: pathname, referrer: typeof document !== "undefined" ? document.referrer || null : null } }).catch(() => {});
  }, [pathname, track]);


  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <ContentProvider>
          {isChrome && <Header />}
          <main className={isChrome ? "pt-16" : ""}>
            <Outlet />
          </main>
          {isChrome && <Footer />}
          {isChrome && <MobileTabBar />}
        </ContentProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
}
